/**
 * API Response Envelope Schemas
 *
 * Zod schemas for our API response envelopes, enabling type-safe response parsing
 * without using `any`.
 */

import { z } from 'zod';

/**
 * Success envelope: { ok: true, data: T }
 */
export const SuccessEnvelopeSchema = z.object({
  ok: z.literal(true),
  data: z.unknown(), // Caller provides specific schema for data
});

/**
 * Error envelope (current format): { ok: false, code, message, details? }
 */
export const ErrorEnvelopeSchema = z.object({
  ok: z.literal(false),
  code: z.string(),
  message: z.string(),
  details: z.unknown().optional(),
});

/**
 * Legacy error format: { error: { message?: string } }
 */
export const LegacyErrorSchema = z.object({
  error: z.object({
    message: z.string().optional(),
  }),
});

/**
 * Generic object schema for checking if response has specific keys
 */
export const GenericObjectSchema = z.record(z.string(), z.unknown());

/**
 * Type-safe discriminated union for API responses
 */
export type ApiResponse =
  | z.infer<typeof SuccessEnvelopeSchema>
  | z.infer<typeof ErrorEnvelopeSchema>
  | z.infer<typeof LegacyErrorSchema>
  | Record<string, unknown>;

/**
 * Type guard to check if a value is an object
 */
export function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Type guard for success envelope
 */
export function isSuccessEnvelope(
  data: unknown
): data is z.infer<typeof SuccessEnvelopeSchema> {
  return isObject(data) && 'ok' in data && data.ok === true && 'data' in data;
}

/**
 * Type guard for error envelope
 */
export function isErrorEnvelope(
  data: unknown
): data is z.infer<typeof ErrorEnvelopeSchema> {
  return (
    isObject(data) &&
    'ok' in data &&
    data.ok === false &&
    'code' in data &&
    'message' in data
  );
}

/**
 * Type guard for legacy error format
 */
export function isLegacyError(
  data: unknown
): data is z.infer<typeof LegacyErrorSchema> {
  return (
    isObject(data) &&
    'error' in data &&
    isObject(data.error) &&
    'message' in data.error
  );
}

/**
 * Type guard for generic error-like object (has 'code' and 'message')
 */
export function isGenericError(
  data: unknown
): data is { code: string; message: string; details?: unknown } {
  return (
    isObject(data) &&
    'code' in data &&
    'message' in data &&
    typeof data.code === 'string' &&
    typeof data.message === 'string'
  );
}
